import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectContact } from 'State/Redux/ContactRedux';
import {
   EndorsementActions,
   selectUpdateEndorsement,
   selectUpdateEndorsementError,
   selectEndorsementHistory,
   EndorsementTypes,
} from 'State/Redux/EndorsementRedux';
import { Button } from 'react-md/lib/Buttons';
import { TextField } from 'react-md/lib/TextFields';
import { SelectField } from 'react-md/lib/SelectFields';
import UpdateEndorsementDto from 'Models/Endorsement/Dto/UpdateEndorsementDto';
import EndorsementDto from 'Models/Endorsement/Dto/EndorsementDto';
import { toast } from 'react-toastify';
import {
   isValidEndDateAfterStartDate,
   isValidDate,
   getInputValidationClassName,
} from 'Util/Helpers/Validation';
import DateInput from 'Components/Common/DateInput/DateInput';
import { isEnterKeyPress } from 'Util/Helpers/Input';
import { selectIsLoading } from 'State/Redux/AsyncRedux';
import LoadingButton from 'Components/Common/LoadingButton';
import { TEXT_MAX_LENGTH_SUSPENSION } from 'Util/Constants/Licence';
import { useEndorsementStatusList } from 'Util/Helpers/Metadata';

interface EditEndorsementProps {
   closeDialog: () => void;
   endorsementId: number;
}

interface ValidationModel {
   endorsementStatusCode: boolean;
   suspensionStartDate: boolean;
   suspensionEndDate: boolean;
   suspensionReason: boolean;
}

const isValidSuspensionDates = (model: UpdateEndorsementDto): boolean => {
   const hasSuspensionDates =
      model.suspensionStartDate && model.suspensionEndDate;
   const hasValidSuspensionRange =
      isValidDate(model.suspensionEndDate) &&
      isValidDate(model.suspensionStartDate) &&
      isValidEndDateAfterStartDate(
         model.suspensionStartDate,
         model.suspensionEndDate
      );

   const hasValidDateRange =
      isValidDate(model.startDate) &&
      isValidDate(model.expiryDate) &&
      isValidEndDateAfterStartDate(model.startDate, model.expiryDate);

   const hasSuspensionRangeWithinDateRange =
      hasValidSuspensionRange &&
      hasValidDateRange &&
      isValidEndDateAfterStartDate(
         model.startDate,
         model.suspensionStartDate
      ) &&
      isValidEndDateAfterStartDate(model.suspensionEndDate, model.expiryDate);

   return !hasSuspensionDates || hasSuspensionRangeWithinDateRange;
};

const EditEndorsement = ({
   closeDialog,
   endorsementId,
}: Readonly<EditEndorsementProps>): JSX.Element => {
   const contact = useSelector(selectContact);
   const endorsements = useSelector(selectEndorsementHistory);
   const updateEndorsementResponse = useSelector(selectUpdateEndorsement);
   const updateEndorsementResponseError = useSelector(
      selectUpdateEndorsementError
   );
   const dispatch = useDispatch();

   const isUpdating = useSelector(
      selectIsLoading([EndorsementTypes.UPDATE_ENDORSEMENT_REQUEST])
   );

   const endorsement = endorsements.find(
      (e: EndorsementDto): boolean => e.endorsementId === endorsementId
   );

   const buildInitialModel = (
      e: EndorsementDto | undefined
   ): UpdateEndorsementDto => ({
      endorsementId: e ? e.endorsementId : -1,
      contactId: contact.contactId,
      endorsementTypeCode: e ? e.endorsementTypeCode : '',
      endorsementStatusCode: e ? e.endorsementStatusCode : '',
      startDate: e ? e.startDate : null,
      expiryDate: e ? e.expiryDate : null,
      suspensionStartDate: e ? e.suspensionStartDate : null,
      suspensionEndDate: e ? e.suspensionEndDate : null,
      suspensionReason: e ? e.suspensionReason : '',
   });

   const [editModel, setEditModel] = useState<UpdateEndorsementDto>(
      buildInitialModel(endorsement)
   );

   const validateModel = (model: UpdateEndorsementDto): ValidationModel => {
      const hasSuspensionEndDate = !!model.suspensionEndDate;
      const hasValidStartDates =
         isValidDate(model.suspensionStartDate);
      const isValidSuspensionStartDate = hasSuspensionEndDate
         ? isValidSuspensionDates(model)
         : hasValidStartDates;

      const hasSuspensionStartDate = !!model.suspensionStartDate;
      const hasValidEndDates =
         isValidDate(model.suspensionEndDate);
      const isValidSuspensionEndDate = hasSuspensionStartDate
         ? isValidSuspensionDates(model)
         : hasValidEndDates;

      return {
         endorsementStatusCode: !!model.endorsementStatusCode,
         suspensionStartDate: isValidSuspensionStartDate,
         suspensionEndDate: isValidSuspensionEndDate,
         suspensionReason:
            !model.suspensionReason ||
            model.suspensionReason.length <= TEXT_MAX_LENGTH_SUSPENSION,
      };
   };

   const [validationModel, setValidationModel] = useState<ValidationModel>(
      validateModel(editModel)
   );

   const isModelValid = Object.values(validationModel).every(v => !!v);

   const updateEditModel = (newState: UpdateEndorsementDto): void => {
      setEditModel(newState);
      setValidationModel(validateModel(newState));
   };

   const saveEndorsement = (): void => {
      if (!isModelValid) {
         toast.error('Please correct the validation errors before saving.');
         return;
      }
      dispatch(EndorsementActions.updateEndorsementRequest(editModel));
   };

   useEffect((): void => {
      if (updateEndorsementResponse !== null) {
         toast.success('Endorsement updated!');
         if (contact.registrationNumber) {
            dispatch(
               EndorsementActions.getEndorsementHistoryByIdRequest(
                  contact.registrationNumber.toString()
               )
            );
         }
         closeDialog();
      }
      if (
         updateEndorsementResponse !== null ||
         updateEndorsementResponseError !== null
      ) {
         dispatch(EndorsementActions.updateEndorsementReset());
      }
   }, [
      updateEndorsementResponse,
      updateEndorsementResponseError,
      closeDialog,
      dispatch,
      contact.registrationNumber,
   ]);

   useEffect((): void => {
      if (updateEndorsementResponseError) {
         toast.error(
            updateEndorsementResponseError.message ||
               'An error occurred while updating the endorsement.'
         );
      }
   }, [updateEndorsementResponseError]);
   
   const endorsementStatuses = useEndorsementStatusList('Select an endorsement status');
   
   return (
      <>
         <div className="md-grid md-cell--12">
            <div className="md-cell md-cell--6">
               <TextField
                  floating
                  id="endorsement-type"
                  label="Endorsement Type"
                  lineDirection="center"
                  className="md-cell md-cell--12"
                  value={endorsement ? endorsement.endorsementTypeDescription : ''}
                  disabled
               />
            </div>
            <div className="md-cell md-cell--6">
               <SelectField
                  id="select-endorsement-status"
                  label="Status"
                  placeholder="Status"
                  className="md-cell md-cell--12"
                  inputClassName={getInputValidationClassName(
                     !validationModel.endorsementStatusCode
                  )}
                  menuItems={endorsementStatuses}
                  itemLabel="label"
                  itemValue="value"
                  position={SelectField.Positions.BELOW}
                  required
                  errorText="Please select a status"
                  error={!validationModel.endorsementStatusCode}
                  value={editModel.endorsementStatusCode}
                  onChange={(val): void => {
                     updateEditModel({
                        ...editModel,
                        endorsementStatusCode: val.toString(),
                     });
                  }}
               />
            </div>
            <div className="md-cell md-cell--6">
               <DateInput
                  id="endorsement-start-date"
                  label="Start Date"
                  className="md-cell md-cell--12"
                  value={editModel.startDate || ''}
                  disabled
               />
            </div>
            <div className="md-cell md-cell--6">
               <DateInput
                  id="endorsement-expiry-date"
                  label="Expiry Date"
                  className="md-cell md-cell--12"
                  value={editModel.expiryDate || ''}
                  disabled
               />
            </div>
            <div className="md-cell md-cell--12">
               <TextField
                  floating
                  id="endorsement-year"
                  label="Licensing Year"
                  lineDirection="center"
                  className="md-cell md-cell--12"
                  value={endorsement ? endorsement.application.licenceYearId : ''}
                  disabled
               />
            </div>
         </div>

         <div className="grey-background md-grid md-cell md-cell--12">
            <div className="md-cell md-cell--12">
               <div className="md-cell md-cell--12">
                  <h2>Suspension Details</h2>
               </div>
            </div>
            <div className="md-cell md-cell--6">
               <DateInput
                  id="suspension-start-date"
                  label="Suspension Start Date"
                  className="md-cell md-cell--12"
                  inputClassName={getInputValidationClassName(
                     !validationModel.suspensionStartDate
                  )}
                  errorText="Please select a valid suspension start date"
                  error={!validationModel.suspensionStartDate}
                  value={editModel.suspensionStartDate || ''}
                  onChange={(date): void => {
                     updateEditModel({ ...editModel, suspensionStartDate: date });
                  }}
               />
            </div>
            <div className="md-cell md-cell--6">
               <DateInput
                  id="suspension-end-date"
                  label="Suspension End Date"
                  className="md-cell md-cell--12"
                  inputClassName={getInputValidationClassName(
                     !validationModel.suspensionEndDate
                  )}
                  errorText="Please select a valid suspension end date"
                  error={!validationModel.suspensionEndDate}
                  value={editModel.suspensionEndDate || ''}
                  onChange={(date): void => {
                     updateEditModel({ ...editModel, suspensionEndDate: date });
                  }}
               />
            </div>
            <div className="md-cell md-cell--12">
               <TextField
                  id="suspension-reason"
                  label="Suspension Reason"
                  placeholder="Suspension Reason"
                  rows={6}
                  maxRows={6}
                  className="md-cell md-cell--12"
                  inputClassName={getInputValidationClassName(
                     !validationModel.suspensionReason
                  )}
                  value={editModel.suspensionReason || ''}
                  error={!validationModel.suspensionReason}
                  maxLength={TEXT_MAX_LENGTH_SUSPENSION}
                  errorText={
                     'Please remove some text, there is a limit of ' +
                     TEXT_MAX_LENGTH_SUSPENSION +
                     ' characters'
                  }
                  onChange={(val): void => {
                     updateEditModel({
                        ...editModel,
                        suspensionReason: val.toString(),
                     });
                  }}
               />
            </div>
         </div>

         <div className="md-cell md-cell--12">
            <LoadingButton
               flat
               isLoading={isUpdating}
               primary
               className="md-cell md-cell--2 md-cell--8-offset"
               onClick={(): void => saveEndorsement()}
               onKeyUp={(keyPress): void => {
                  if (isEnterKeyPress(keyPress)) saveEndorsement();
               }}
               swapTheming
               disabled={!isModelValid}
            >
               Update
            </LoadingButton>
            <Button
               flat
               secondary
               className="red-btn md-cell md-cell--2"
               onClick={(): void => {
                  closeDialog();
               }}
               onKeyUp={(keyPress): void => {
                  if (isEnterKeyPress(keyPress)) closeDialog();
               }}
               swapTheming
            >
               Cancel
            </Button>
         </div>
      </>
   );
};

export default React.memo(EditEndorsement);
